<template src="./quiz-config-practice.html"></template>

<script>
  import appHeader from "../../../shared-module/components/app-header/app-header.vue";
  import storageService from "../../services/storage-service";
  import quizConfigurationService from "../../services/quiz-configuration-service";
  import quizConfigPracticeService from "../../services/quiz-config-practice-service";
  import _ from "lodash";

  export default {
    name: "app-quiz-config-practice",
    components: {
      "app-header": appHeader,
    },
    directives: {
      'click-outside': {
        bind(el, binding) {
          el._clickOutsideHandler = (event) => {
            if (!(el === event.target || el.contains(event.target))) {
              binding.value(event);
            }
          };
          document.addEventListener('click', el._clickOutsideHandler);
        },
        unbind(el) {
          document.removeEventListener('click', el._clickOutsideHandler);
        }
      }
    },
    data() {
      return {
        // Dashboard table data
        configurations: [],
        isLoading: false,

        // Inline name editing
        editingNameIndex: null,
        editingNameValue: "",

        // Create / Edit modal
        showCreateModal: false,
        isEditMode: false,
        editingConfigId: null,
        modalConfigName: "",
        isSaving: false,

        // Niche selector
        selectedNiche: "",
        selectedNicheId: null,
        showNicheDropdown: false,
        nicheOptions: [],
        nicheSearchQuery: "",

        // Subject multi-select
        selectedSubjects: [],
        showSubjectDropdown: false,
        availableSubjects: [],
        subjectSearchQuery: "",
        isLoadingSubjects: false,
        isBatchedDetailsLoading: false,
        // Hierarchical tree state: keyed by subject id
        treeState: {},

        // Duplicate modal
        showDuplicateModal: false,
        duplicateConfig: null,
        duplicateName: "",

        // Hide modal
        showHideModal: false,
        hideConfig: null,
        hideConfigName: "",

        // Success modal
        showSuccessModal: false,

        // Toast & Error state
        toast: {
          show: false,
          message: "",
          type: "success"
        },
        showErrorModal: false,
        errorMessage: "",
      };
    },
    created() {
      this.fetchNiches();
      this.fetchConfigurations();
    },
    methods: {
      async fetchConfigurations() {
        try {
            this.isLoading = true;
            const response = await quizConfigPracticeService.getAll("", 1, 100);
            if (response && response.data && response.data.success) {
                this.configurations = response.data.docs || [];
            }
        } catch (error) {
            console.error("Error fetching masked configurations:", error);
            this.triggerToast("Failed to load configurations", "error");
        } finally {
            this.isLoading = false;
        }
      },

      async fetchNiches() {
        try {
            const response = await quizConfigurationService.getCourseTags();
            if (response && response.data && response.data.success) {
                const tags = response.data.courseTags.map(tag => ({ id: tag.id, name: tag.name }));
                this.nicheOptions = [{ id: null, name: "-- None --" }, ...tags];
            }
        } catch (error) {
            console.error("Error fetching niches:", error);
            this.triggerToast("Failed to load niches", "error");
        }
      },
      
      async fetchAvailableSubjects(nicheId = null) {
        try {
            this.isLoadingSubjects = true;
            const response = await quizConfigurationService.getAllConfigurations("", "", false, 1, 1000, nicheId);
            if (response && response.data && response.data.docs) {
                this.availableSubjects = response.data.docs.map(doc => ({
                    id: doc._id,
                    name: doc.name
                }));
            } else {
                this.availableSubjects = [];
            }
        } catch (error) {
            console.error("Error fetching available subjects:", error);
            this.triggerToast("Failed to load subjects", "error");
            this.availableSubjects = [];
        } finally {
            this.isLoadingSubjects = false;
        }
      },

      // ---- Placeholder for create quiz popup (passed to header) ----
      createQuizPopupHandler() {},

      // ---- Inline Name Editing ----
      startEditName(index) {
        this.editingNameIndex = index;
        this.editingNameValue =
          this.configurations[index].displayName ||
          this.configurations[index].name;
        this.$nextTick(() => {
          if (this.$refs.nameEditInput && this.$refs.nameEditInput[0]) {
            this.$refs.nameEditInput[0].focus();
            this.$refs.nameEditInput[0].select();
          }
        });
      },
      async saveConfigName(index) {
        const config = this.configurations[index];
        if (this.editingNameValue.trim() !== "") {
          config.displayName = this.editingNameValue;
          try {
              await quizConfigPracticeService.update(config.id, { displayName: this.editingNameValue });
              this.fetchConfigurations();
              this.triggerToast("Name updated successfully");
          } catch (error) {
              console.error("Error updating name:", error);
              this.triggerToast(this.getErrorMessage(error, "Error updating name"), "error");
          }
        }
        this.editingNameIndex = -1;
        this.editingNameValue = "";
      },

      // ---- Notifications ----
      triggerToast(message, type = "success") {
        this.toast.message = message;
        this.toast.type = type;
        this.toast.show = true;
        setTimeout(() => {
          this.toast.show = false;
        }, 3000);
      },
      triggerError(message) {
        this.errorMessage = message;
        this.showErrorModal = true;
      },
      closeErrorModal() {
        this.showErrorModal = false;
        this.errorMessage = "";
      },
      getErrorMessage(error, defaultMsg = "An error occurred") {
        if (error.response && error.response.data && error.response.data.message) {
          return error.response.data.message;
        }
        return defaultMsg;
      },
      cancelEditName() {
        this.editingNameIndex = null;
        this.editingNameValue = "";
      },

      // ---- Create / Edit Modal ----
      openCreateModal() {
        this.isEditMode = false;
        this.editingConfigId = null;
        this.modalConfigName = "";
        this.selectedNiche = "";
        this.selectedSubjects = [];
        this.treeState = {};
        this.showCreateModal = true;
      },
      closeCreateModal() {
        this.showCreateModal = false;
      },

      // ---- editConfig: Uses findOne (masked-configuration API) instead of getConfiguration ----
      async editConfig(config) {
        this.isEditMode = true;
        this.editingConfigId = config.id || config._id;
        this.modalConfigName = config.name;

        // Match Niche
        this.selectedNicheId = config.niche_id;
        const nicheObj = this.nicheOptions.find(o => o.id == config.niche_id);
        this.selectedNiche = nicheObj ? nicheObj.name : "";

        // Pre-fill availableSubjects with selected configurations so names are visible immediately
        if (config.configurations) {
            this.availableSubjects = config.configurations.map(c => ({
                id: c._id,
                name: c.name
            }));
        }

        // Fetch all subjects for this niche so dropdown shows all options
        await this.fetchAvailableSubjects(this.selectedNicheId);

        this.selectedSubjects = [];
        this.treeState = {};

        // Show modal immediately so loader is visible
        this.showCreateModal = true;
        this.isBatchedDetailsLoading = true;

        try {
            // Use findOne (masked-configuration API) instead of getConfiguration
            const response = await quizConfigPracticeService.findOne(this.editingConfigId);
            const maskedConfig = response.data.configuration;

            // Use the name from the full masked config response
            this.modalConfigName = maskedConfig.name || this.modalConfigName;

            // Extract difficulty levels
            const globalDiffs = maskedConfig.difficulty_levels && maskedConfig.difficulty_levels.length
                ? maskedConfig.difficulty_levels.map(d => d.tag_name ? d.tag_name.toLowerCase() : 'easy')
                : ['easy'];

            if (maskedConfig.configurations && maskedConfig.configurations.length > 0) {
                maskedConfig.configurations.forEach(conf => {
                    // Use conf._id as the key (NOT base_configuration._id, 
                    // because multiple configs can share the same base_configuration)
                    const subId = conf._id;
                    const subName = conf.name;

                    this.selectedSubjects.push(subId);

                    const topics = {};
                    let subjectQCount = 0;

                    if (conf.topics) {
                        conf.topics.forEach(t => {
                            const subtopics = {};
                            let topicQCount = t.questionCount || 0;

                            if (t.sub_topics) {
                                t.sub_topics.forEach(st => {
                                    subtopics[st._id] = {
                                        id: st._id,
                                        name: st.name,
                                        qCount: st.questionCount || 0,
                                        difficulty: [...globalDiffs],
                                        checked: true  // Present in response = selected
                                    };
                                });
                            }

                            subjectQCount += topicQCount;

                            topics[t._id] = {
                                id: t._id,
                                name: t.name,
                                qCount: topicQCount,
                                subtopics: subtopics,
                                difficulty: [...globalDiffs],
                                checked: true,  // Present in response = selected
                                isExpanded: false
                            };
                        });
                    }

                    this.$set(this.treeState, subId, {
                        id: subId,
                        name: subName,
                        qCount: subjectQCount,
                        topics: topics,
                        difficulty: [...globalDiffs],
                        checked: true,
                        isExpanded: true,
                        loaded: true
                    });
                });
            }
        } catch (error) {
            console.error("Error loading masked config for edit:", error);
            this.triggerToast("Failed to load configuration details", "error");
        } finally {
            this.isBatchedDetailsLoading = false;
        }
      },

      async saveConfig() {
        if (this.isSaving) {
            return;
        }
        if (!this.modalConfigName.trim()) {
            this.triggerToast("Please enter a mask configuration name", "error");
            return;
        }
        if (!this.selectedNicheId) {
            this.triggerToast("Please select a niche", "error");
            return;
        }
        if (this.selectedSubjects.length === 0) {
            this.triggerToast("Please select at least one subject", "error");
            return;
        }

        const configurations = [...this.selectedSubjects];
        const topicsSet = new Set();
        const subtopicsSet = new Set();
        const difficultiesSet = new Set();

        this.selectedSubjects.forEach(subId => {
            const state = this.treeState[subId];
            if (state) {
                if (state.difficulty) state.difficulty.forEach(d => difficultiesSet.add(d));
                if (state.topics) {
                    Object.values(state.topics).forEach(t => {
                        let hasCheckedSubtopics = false;
                        if (t.subtopics) {
                            Object.values(t.subtopics).forEach(st => {
                                if (st.checked) {
                                    subtopicsSet.add(st.id);
                                    hasCheckedSubtopics = true;
                                    if (st.difficulty) st.difficulty.forEach(d => difficultiesSet.add(d));
                                }
                            });
                        }

                        if (t.checked || hasCheckedSubtopics) {
                            topicsSet.add(t.id);
                            if (t.difficulty) t.difficulty.forEach(d => difficultiesSet.add(d));
                        }
                    });
                }
            }
        });

        // Collect renamed items
        const renamed = this.getRenamedItems();

        const payload = {
            name: this.modalConfigName,
            niche_id: this.selectedNicheId,
            configurations,
            topics: Array.from(topicsSet),
            subtopics: Array.from(subtopicsSet),
            difficulty_levels: Array.from(difficultiesSet).map(d => d.charAt(0).toUpperCase() + d.slice(1)),
            disabled: false
        };

        // Include renamed items if any
        if (renamed.editedConfigs.length > 0) {
            payload.masked_configurations = renamed.editedConfigs;
        }
        if (renamed.editedTopics.length > 0) {
            payload.masked_topics = renamed.editedTopics;
        }
        if (renamed.editedSubtopics.length > 0) {
            payload.masked_subtopics = renamed.editedSubtopics;
        }

        this.isSaving = true;
        try {
            if (this.isEditMode && this.editingConfigId) {
                await quizConfigPracticeService.update(this.editingConfigId, payload);
            } else {
                await quizConfigPracticeService.create(payload);
            }
            this.showCreateModal = false;
            this.showSuccessModal = true;
            this.fetchConfigurations();
        } catch (error) {
            console.error("Error saving configuration:", error);
            const msg = this.getErrorMessage(error, "Failed to save configuration");
            this.triggerError(msg);
        } finally {
            this.isSaving = false;
        }
      },

      // ---- Niche Selector ----
      selectNiche(nicheObj) {
        if (nicheObj.id === null) {
            this.selectedNiche = "";
            this.selectedNicheId = null;
        } else {
            this.selectedNiche = nicheObj.name;
            this.selectedNicheId = nicheObj.id;
        }
        this.showNicheDropdown = false;
        this.nicheSearchQuery = "";
        
        // Clear selected subjects as they might not belong to new niche
        this.selectedSubjects = [];
        this.treeState = {};
        
        // Fetch subjects for this niche
        this.fetchAvailableSubjects(this.selectedNicheId);
      },
      hideNicheDropdown() {
        this.showNicheDropdown = false;
        this.nicheSearchQuery = "";
      },

      // ---- Subject Multi-select ----
      getSubjectName(id) {
        // Check treeState first (may have renamed name)
        if (this.treeState[id] && this.treeState[id].name) {
            return this.treeState[id].name;
        }
        const sub = this.availableSubjects.find((s) => s.id === id);
        return sub ? sub.name : id;
      },
      getSubjectData(id) {
        return this.treeState[id] || {};
      },
      
      async loadSubjectDetails(configFileId) {
        this.isBatchedDetailsLoading = true;

        const isBatch = Array.isArray(configFileId);
        const idsToLoad = isBatch ? configFileId : [configFileId];

        // Filter out already loaded
        const missingIds = idsToLoad.filter(id => !this.treeState[id] || !this.treeState[id].loaded);
        if (missingIds.length === 0) {
            this.isBatchedDetailsLoading = false;
            return;
        }

        // Initialize placeholders if they don't exist
        missingIds.forEach(id => {
            if (!this.treeState[id]) {
                this.$set(this.treeState, id, {
                    id: id,
                    name: this.getSubjectName(id), 
                    qCount: 0,
                    topics: {},
                    difficulty: ['easy'], 
                    checked: true,
                    isExpanded: true,
                    loaded: false
                });
            }
        });

        try {
            const response = await quizConfigurationService.getConfiguration(configFileId, true);
            
            let configs = [];
            if (response && response.data) {
                if (Array.isArray(response.data.configurations)) {
                     configs = response.data.configurations;
                } else if (response.data.configuration) {
                     configs = Array.isArray(response.data.configuration) 
                        ? response.data.configuration 
                        : [response.data.configuration];
                }
            }

            configs.forEach(config => {
                const actualId = config._id;
                if (!actualId || !this.treeState[actualId]) return;

                const topics = {};
                let subjectQCount = 0;
                if (config.topics) {
                  config.topics.forEach(t => {
                      const subtopics = {};
                      let topicQCount = t.questionCount || 0; 
                      
                      if (t.sub_topics) {
                          t.sub_topics.forEach(st => {
                              subtopics[st._id] = { 
                                  id: st._id,
                                  name: st.name,
                                  qCount: st.questionCount || 0,
                                  difficulty: ['easy'],
                                  checked: true
                              };
                          });
                      }

                      subjectQCount += topicQCount;

                      topics[t._id] = { 
                          id: t._id,
                          name: t.name,
                          qCount: topicQCount, 
                          subtopics: subtopics,
                          difficulty: ['easy'],
                          checked: true,
                          isExpanded: false
                      };
                  });
                }

                const updatedState = {
                    ...this.treeState[actualId],
                    name: config.name, 
                    qCount: subjectQCount, 
                    topics: topics,
                    difficulty: this.treeState[actualId].difficulty && this.treeState[actualId].difficulty.length > 0 
                        ? this.treeState[actualId].difficulty 
                        : ['easy'],
                    checked: true, 
                    loaded: true
                };
                this.$set(this.treeState, actualId, updatedState);
            });
        } catch (error) {
            console.error("Error loading subject details:", error);
            this.triggerToast("Failed to load configuration details", "error");
        } finally {
            this.isBatchedDetailsLoading = false;
        }
      },

      async toggleSubject(id) {
        const idx = this.selectedSubjects.indexOf(id);
        if (idx === -1) {
          this.selectedSubjects.push(id);
          if (!this.treeState[id]) {
             this.$set(this.treeState, id, {
                id: id,
                name: this.getSubjectName(id), 
                qCount: 0,
                topics: {},
                difficulty: ['easy'],
                checked: true,
                isExpanded: true,
                loaded: false
             });
          }
          await this.loadSubjectDetails(id); 
        } else {
          this.selectedSubjects.splice(idx, 1);
          this.$delete(this.treeState, id);
        }
      },
      removeSubject(id) {
        const idx = this.selectedSubjects.indexOf(id);
        if (idx !== -1) {
          this.selectedSubjects.splice(idx, 1);
          this.$delete(this.treeState, id);
        }
      },
      toggleSubjectDropdown() {
        this.showSubjectDropdown = !this.showSubjectDropdown;
        if (!this.showSubjectDropdown) {
          this.subjectSearchQuery = "";
        }
      },
      hideSubjectDropdown() {
        this.showSubjectDropdown = false;
        this.subjectSearchQuery = "";
      },

      // ---- Tree State Management ----

      getTreeState(subId) {
        return this.treeState[subId] || { checked: false, difficulty: ["medium"], isExpanded: false, topics: {} };
      },
      getTopicState(subId, topicId) {
        const s = this.treeState[subId];
        return s && s.topics[topicId]
          ? s.topics[topicId]
          : { checked: false, difficulty: ["medium"], isExpanded: false, subtopics: {} };
      },
      getSubtopicState(subId, topicId, stId) {
        const t = this.getTopicState(subId, topicId);
        return t.subtopics && t.subtopics[stId]
          ? t.subtopics[stId]
          : { checked: false, difficulty: ["medium"] };
      },

      // ---- Expand/Collapse ----
      toggleExpand(subId, level, topicId) {
        if (level === "subject") {
          this.treeState[subId].isExpanded = !this.treeState[subId].isExpanded;
        } else if (level === "topic") {
          this.treeState[subId].topics[topicId].isExpanded =
            !this.treeState[subId].topics[topicId].isExpanded;
        }
        this.$set(this.treeState, subId, { ...this.treeState[subId] });
      },

      // ---- Checkbox Toggle ----
      toggleCheck(subId, level, topicId, stId) {
        const state = this.treeState[subId];
        if (level === "subject") {
          const newVal = !state.checked;
          state.checked = newVal;
          Object.values(state.topics).forEach((t) => {
            t.checked = newVal;
            Object.values(t.subtopics).forEach((st) => (st.checked = newVal));
          });
        } else if (level === "topic") {
          const t = state.topics[topicId];
          const newVal = !t.checked;
          t.checked = newVal;
          Object.values(t.subtopics).forEach((st) => (st.checked = newVal));
          state.checked = Object.values(state.topics).every((tp) => tp.checked);
        } else if (level === "subtopic") {
          const t = state.topics[topicId];
          const st = t.subtopics[stId];
          st.checked = !st.checked;
          t.checked = Object.values(t.subtopics).every((s) => s.checked);
          state.checked = Object.values(state.topics).every((tp) => tp.checked);
        }
        this.$set(this.treeState, subId, { ...state });
      },

      // ---- Difficulty Pills ----
      setDifficulty(subId, level, diff, topicId, stId) {
        const state = this.treeState[subId];

        const updateList = (list, d) => {
          const idx = list.indexOf(d);
          if (idx > -1) {
            if (list.length > 1) list.splice(idx, 1);
          } else {
            list.push(d);
          }
          return list;
        };

        if (level === "subject") {
          state.difficulty = updateList([...state.difficulty], diff);
          const nl = [...state.difficulty];
          Object.values(state.topics).forEach((t) => {
            t.difficulty = [...nl];
            Object.values(t.subtopics).forEach((st) => (st.difficulty = [...nl]));
          });
        } else if (level === "topic") {
          const t = state.topics[topicId];
          t.difficulty = updateList([...t.difficulty], diff);
          const nl = [...t.difficulty];
          Object.values(t.subtopics).forEach((st) => (st.difficulty = [...nl]));
        } else if (level === "subtopic") {
          const t = state.topics[topicId];
          t.subtopics[stId].difficulty = updateList(
            [...t.subtopics[stId].difficulty],
            diff
          );
        }

        this.$set(this.treeState, subId, { ...state });
      },

      // ---- Duplicate Modal ----
      openDuplicateModal(config) {
        this.duplicateConfig = config;
        this.duplicateName = config.name + " (Copy)";
        this.showDuplicateModal = true;
        this.$nextTick(() => {
          if (this.$refs.duplicateInput) {
            this.$refs.duplicateInput.focus();
            this.$refs.duplicateInput.select();
          }
        });
      },
      closeDuplicateModal() {
        this.showDuplicateModal = false;
        this.duplicateConfig = null;
        this.duplicateName = "";
      },
      async confirmDuplicate() {
        if (!this.duplicateName.trim() || !this.duplicateConfig) return;
        
        try {
          const payload = {
            name: this.duplicateName.trim(),
            niche_id: this.duplicateConfig.niche_id,
            configurations: this.duplicateConfig.configurations.map(c => c._id || c.id),
            topics: this.duplicateConfig.topics && Array.isArray(this.duplicateConfig.topics) 
                ? this.duplicateConfig.topics.map(t => t._id || t.id || t) 
                : [],
            subtopics: this.duplicateConfig.subtopics && Array.isArray(this.duplicateConfig.subtopics) 
                ? this.duplicateConfig.subtopics.map(st => st._id || st.id || st) 
                : [],
            difficulty_levels: this.duplicateConfig.difficulty_levels && this.duplicateConfig.difficulty_levels.length 
              ? this.duplicateConfig.difficulty_levels.map(d => d.tag_name) 
              : ["Easy"],
            disabled: false
          };

          await quizConfigPracticeService.create(payload);
          this.triggerToast("Configuration duplicated successfully");
          this.fetchConfigurations();
          this.closeDuplicateModal();
        } catch (error) {
          console.error("Error duplicating config:", error);
          const msg = this.getErrorMessage(error, "Failed to duplicate configuration");
          this.triggerToast(msg, "error");
        }
      },

      // ---- Hide Modal ----
      openHideModal(config) {
        console.log("🚀 ~ config:", config)
        this.hideConfig = config;
        this.hideConfigName = config.displayName || config.name;
        this.showHideModal = true;
      },
      closeHideModal() {
        this.showHideModal = false;
        this.hideConfig = null;
        this.hideConfigName = "";
      },
      async confirmHide() {
        if (this.hideConfig) {
           try {
               await quizConfigPracticeService.update(this.hideConfig._id, { disabled: true });
               this.fetchConfigurations();
            } catch (error) {
                console.error("Error hiding config:", error);
                this.triggerToast(this.getErrorMessage(error, "Error hiding configuration"), "error");
            }
        }
        this.closeHideModal();
      },
      async toggleConfigVisibility(config) {
        try {
            await quizConfigPracticeService.update(config._id, { disabled: !config.disabled });
            this.fetchConfigurations();
        } catch (error) {
            console.error("Error toggling visibility:", error);
            this.triggerToast(this.getErrorMessage(error, "Error toggling visibility"), "error");
        }
      },

      // ---- Renaming Logic ----
      toggleRename(id, type) {
          if (type === 'subject') {
              const state = this.treeState[id];
              if (state) {
                  this.$set(state, 'isRenaming', !state.isRenaming);
                  if (state.isRenaming) {
                      this.$set(state, 'tempName', state.name);
                      this.$nextTick(() => {
                          const input = this.$refs[`renameInput_${id}`];
                          if (input && input[0]) input[0].focus();
                      });
                  }
              }
          } else if (type === 'topic') {
               for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics && subState.topics[id]) {
                       const topic = subState.topics[id];
                       this.$set(topic, 'isRenaming', !topic.isRenaming);
                       if (topic.isRenaming) {
                           this.$set(topic, 'tempName', topic.name);
                            this.$nextTick(() => {
                                const input = this.$refs[`renameInput_${id}`];
                                if (input && input[0]) input[0].focus();
                            });
                       }
                       break;
                   }
               }
          } else if (type === 'subtopic') {
              for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics) {
                       for (const topicId in subState.topics) {
                           const topic = subState.topics[topicId];
                           if (topic.subtopics && topic.subtopics[id]) {
                               const subtopic = topic.subtopics[id];
                               this.$set(subtopic, 'isRenaming', !subtopic.isRenaming);
                               if (subtopic.isRenaming) {
                                   this.$set(subtopic, 'tempName', subtopic.name);
                                    this.$nextTick(() => {
                                        const input = this.$refs[`renameInput_${id}`];
                                        if (input && input[0]) input[0].focus();
                                    });
                               }
                               return;
                           }
                       }
                   }
               }
          }
      },
      saveRename(id, type) {
          if (type === 'subject') {
              const state = this.treeState[id];
              if (state && state.tempName.trim()) {
                  state.name = state.tempName.trim();
                  state.isRenaming = false;
                  this.$set(state, 'isEdited', true);
              }
          } else if (type === 'topic') {
               for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics && subState.topics[id]) {
                       const topic = subState.topics[id];
                       if (topic.tempName.trim()) {
                           topic.name = topic.tempName.trim();
                           topic.isRenaming = false;
                           this.$set(topic, 'isEdited', true);
                       }
                       break;
                   }
               }
          } else if (type === 'subtopic') {
              for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics) {
                       for (const topicId in subState.topics) {
                           const topic = subState.topics[topicId];
                           if (topic.subtopics && topic.subtopics[id]) {
                               const subtopic = topic.subtopics[id];
                               if (subtopic.tempName.trim()) {
                                   subtopic.name = subtopic.tempName.trim();
                                   subtopic.isRenaming = false;
                                   this.$set(subtopic, 'isEdited', true);
                               }
                               return; 
                           }
                       }
                   }
               }
          }
      },
      cancelRename(id, type) {
          if (type === 'subject') {
              const state = this.treeState[id];
              if (state) state.isRenaming = false;
          } else if (type === 'topic') {
              for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics && subState.topics[id]) {
                       subState.topics[id].isRenaming = false;
                       break;
                   }
               }
          } else if (type === 'subtopic') {
              for (const subId in this.treeState) {
                   const subState = this.treeState[subId];
                   if (subState.topics) {
                       for (const topicId in subState.topics) {
                           const topic = subState.topics[topicId];
                           if (topic.subtopics && topic.subtopics[id]) {
                               topic.subtopics[id].isRenaming = false;
                               return; 
                           }
                       }
                   }
               }
          }
      },
      // Collect renamed items for payload
      getRenamedItems() {
          const editedConfigs = [];
          const editedTopics = [];
          const editedSubtopics = [];

          for (const subId in this.treeState) {
              const subState = this.treeState[subId];
              if (subState.isEdited) {
                  editedConfigs.push({ _id: subState.id, name: subState.name });
              }
              if (subState.topics) {
                  for (const topicId in subState.topics) {
                      const topic = subState.topics[topicId];
                      if (topic.isEdited) {
                          editedTopics.push({ _id: topic.id, name: topic.name });
                      }
                      if (topic.subtopics) {
                          for (const stId in topic.subtopics) {
                              const st = topic.subtopics[stId];
                              if (st.isEdited) {
                                  editedSubtopics.push({ _id: st.id, name: st.name });
                              }
                          }
                      }
                  }
              }
          }
          return { editedConfigs, editedTopics, editedSubtopics };
      },
    },
    computed: {
      filteredSubjects() {
        if (!this.subjectSearchQuery) {
          return this.availableSubjects;
        }
        const query = this.subjectSearchQuery.toLowerCase();
        return this.availableSubjects.filter((sub) =>
          sub.name.toLowerCase().includes(query)
        );
      },
      filteredNiches() {
        if (!this.nicheSearchQuery) {
          return this.nicheOptions;
        }
        const query = this.nicheSearchQuery.toLowerCase();
        return this.nicheOptions.filter((niche) =>
          niche.name.toLowerCase().includes(query)
        );
      },
    },
  };
</script>

<style src="./quiz-config-practice.css" scoped></style>
